import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { ascoltaCoda } from "@/lib/codaCaricamenti";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Oggi from "@/pages/Oggi";
import Task from "@/pages/Task";
import AttivitaOperative from "@/pages/AttivitaOperative";
import Notizie from "@/pages/Notizie";
import NotiziaDetail from "@/pages/NotiziaDetail";
import Pipeline from "@/pages/Pipeline";
import Immobili from "@/pages/Immobili";
import ImmobileDetail from "@/pages/ImmobileDetail";
import Acquirenti from "@/pages/Acquirenti";
import Proprietari from "@/pages/Proprietari";
import Visite from "@/pages/Visite";
import Proposte from "@/pages/Proposte";
import Preliminari from "@/pages/Preliminari";
import Rogiti from "@/pages/Rogiti";
import DocumentiScadenza from "@/pages/DocumentiScadenza";
import FinanzaAgente from "@/pages/FinanzaAgente";
import ProvvigioniTeam from "@/pages/ProvvigioniTeam";
import Agenda from "@/pages/Agenda";import Classifica from "@/pages/Classifica";
import AlboOro from "@/pages/AlboOro";
import Finanza from "@/pages/Finanza";
import Documenti from "@/pages/Documenti";
import ArchivioDocumenti from "@/pages/ArchivioDocumenti";
import Formazione from "@/pages/Formazione";
import FormazioneScheda from "@/pages/FormazioneScheda";
import Riunioni from "@/pages/Riunioni";
import RiunioneDetail from "@/pages/RiunioneDetail";
import LeMieRichieste from "@/pages/LeMieRichieste";
import Azioni from "@/pages/Azioni";
import Messaggi from "@/pages/Messaggi";
import AzioneDetail from "@/pages/AzioneDetail";
import CentraleElenco from "@/pages/CentraleElenco";
import ObiettiviTeam from "@/pages/ObiettiviTeam";
import FotografoServizi from "@/pages/FotografoServizi";
import LeMieFatture from "@/pages/LeMieFatture";
import Marketing from "@/pages/Marketing";
import FotoImmobile from "@/pages/FotoImmobile";
import DatiDaSistemare from "@/pages/DatiDaSistemare";
import { installaMaiuscole } from "@/lib/formato";
installaMaiuscole();
import { PubRichiesta, PubCondivisione } from "@/pages/PubblicoDocumenti";
import Storico from "@/pages/Storico";
import Privacy from "@/pages/Privacy";
import Utenti from "@/pages/Utenti";
import PuliziaDati from "@/pages/PuliziaDati";
import ModelliDocumenti from "@/pages/ModelliDocumenti";
import QualitaPortafoglio from "@/pages/QualitaPortafoglio";
import FineGiornata from "@/pages/FineGiornata";
import Tickets from "@/pages/Tickets";
import TicketDetail from "@/pages/TicketDetail";
import Profilo from "@/pages/Profilo";
import GuardianaDati from "@/pages/GuardianaDati";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Sessioni from "@/pages/Sessioni";
import CodaChecklist from "@/pages/CodaChecklist";
import DocumentazioneInCorso from "@/pages/DocumentazioneInCorso";
import ProntiPubblicazione from "@/pages/ProntiPubblicazione";
import AppuntamentiOggi from "@/pages/AppuntamentiOggi";
import ForcePasswordChange from "@/pages/ForcePasswordChange";
import DaApprovare from "@/pages/DaApprovare";
import DirezioneProvvigioni from "@/pages/DirezioneProvvigioni";
import { TwoFactorEnroll } from "@/components/TwoFactor";
import { Loader2 } from "lucide-react";

function Protected({ children, financeOnly, direzioneOnly, usersAdmin, segreteriaOnly, privacy }) {
  const { user, booting, canFinance, isDirezione, canManageUsers, isSegreteria } = useAuth();
  if (booting) return <div className="min-h-screen flex items-center justify-center bg-white"><Loader2 className="animate-spin text-[#FC82ED]" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForcePasswordChange />;
  if (user.mfa_enrollment_required) return <TwoFactorEnroll />;
  if (privacy && !(isDirezione || isSegreteria)) return <Navigate to="/oggi" replace />;
  if (financeOnly && !canFinance) return <Navigate to="/oggi" replace />;
  if (direzioneOnly && !isDirezione) return <Navigate to="/oggi" replace />;
  if (segreteriaOnly && !isSegreteria) return <Navigate to="/oggi" replace />;
  if (usersAdmin && !canManageUsers) return <Navigate to="/oggi" replace />;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/cliente/richiesta/:token" element={<PubRichiesta />} />
      <Route path="/condivisione/:token" element={<PubCondivisione />} />
      <Route path="/oggi" element={<Protected><Oggi /></Protected>} />
      <Route path="/task" element={<Protected><Task /></Protected>} />
      <Route path="/attivita" element={<Protected><AttivitaOperative /></Protected>} />
      <Route path="/notizie" element={<Protected><Notizie /></Protected>} />
      <Route path="/notizie/:id" element={<Protected><NotiziaDetail /></Protected>} />
      <Route path="/pipeline" element={<Protected><Pipeline /></Protected>} />
      <Route path="/immobili" element={<Protected><Immobili /></Protected>} />
      <Route path="/immobili/:id" element={<Protected><ImmobileDetail /></Protected>} />
      <Route path="/acquirenti" element={<Protected><Acquirenti /></Protected>} />
      <Route path="/proprietari" element={<Protected><Proprietari /></Protected>} />
      <Route path="/visite" element={<Protected><Visite /></Protected>} />
      <Route path="/proposte" element={<Protected><Proposte /></Protected>} />
      <Route path="/preliminari" element={<Protected><Preliminari /></Protected>} />
      <Route path="/rogiti" element={<Protected><Rogiti /></Protected>} />
      <Route path="/documenti-scadenza" element={<Protected><DocumentiScadenza /></Protected>} />
      <Route path="/finanza-agente" element={<Protected><FinanzaAgente /></Protected>} />
      <Route path="/provvigioni-team" element={<Protected><ProvvigioniTeam /></Protected>} />
      <Route path="/agenda" element={<Protected><Agenda /></Protected>} />
      <Route path="/documenti" element={<Protected><Documenti /></Protected>} />
      <Route path="/archivio-documenti" element={<Protected><ArchivioDocumenti /></Protected>} />
      <Route path="/formazione" element={<Protected><Formazione /></Protected>} />
      <Route path="/formazione/:id" element={<Protected><FormazioneScheda /></Protected>} />
      <Route path="/riunioni" element={<Protected><Riunioni /></Protected>} />
      <Route path="/riunioni/:id" element={<Protected><RiunioneDetail /></Protected>} />
      <Route path="/le-mie-richieste" element={<Protected><LeMieRichieste /></Protected>} />
      <Route path="/azioni" element={<Navigate to="/task" replace />} />
      <Route path="/messaggi" element={<Protected><Messaggi /></Protected>} />
      <Route path="/azioni/:id" element={<Navigate to="/task" replace />} />
      <Route path="/centrale/elenco" element={<Protected><CentraleElenco /></Protected>} />
      <Route path="/obiettivi-team" element={<Protected><ObiettiviTeam /></Protected>} />
      <Route path="/fotografo" element={<Protected><FotografoServizi /></Protected>} />
      <Route path="/fotografo/:id" element={<Protected><FotografoServizi /></Protected>} />
      <Route path="/fatture" element={<Protected><LeMieFatture /></Protected>} />
      <Route path="/marketing" element={<Protected><Marketing /></Protected>} />
      <Route path="/marketing/foto/:id" element={<Protected><FotoImmobile /></Protected>} />
      <Route path="/dati-da-sistemare" element={<Protected privacy><DatiDaSistemare /></Protected>} />
      <Route path="/classifica" element={<Protected><Classifica /></Protected>} />
      <Route path="/albo-oro" element={<Protected><AlboOro /></Protected>} />
      <Route path="/da-approvare" element={<Protected><DaApprovare /></Protected>} />
      <Route path="/direzione-provvigioni" element={<Protected><DirezioneProvvigioni /></Protected>} />
      <Route path="/profilo" element={<Protected><Profilo /></Protected>} />
      <Route path="/sessioni" element={<Protected direzioneOnly><Sessioni /></Protected>} />
      <Route path="/pulizia-dati" element={<Protected direzioneOnly><PuliziaDati /></Protected>} />
      <Route path="/modelli-documenti" element={<Protected direzioneOnly><ModelliDocumenti /></Protected>} />
      <Route path="/qualita-portafoglio" element={<Protected financeOnly><QualitaPortafoglio /></Protected>} />
      <Route path="/fine-giornata" element={<Protected><FineGiornata /></Protected>} />
      <Route path="/coda-checklist" element={<Protected segreteriaOnly><CodaChecklist /></Protected>} />
      <Route path="/documentazione-in-corso" element={<Protected><DocumentazioneInCorso /></Protected>} />
      <Route path="/pronti-pubblicazione" element={<Protected><ProntiPubblicazione /></Protected>} />
      <Route path="/appuntamenti-oggi" element={<Protected><AppuntamentiOggi /></Protected>} />
      <Route path="/finanza" element={<Protected financeOnly><Finanza /></Protected>} />
      <Route path="/utenti" element={<Protected usersAdmin><Utenti /></Protected>} />
      <Route path="/ticket" element={<Protected><Tickets /></Protected>} />
      <Route path="/ticket/:id" element={<Protected><TicketDetail /></Protected>} />
      <Route path="/storico" element={<Protected direzioneOnly><Storico /></Protected>} />
      <Route path="/privacy" element={<Protected privacy><Privacy /></Protected>} />
      <Route path="/guardiana" element={<Protected privacy><GuardianaDati /></Protected>} />
      <Route path="*" element={<Navigate to="/oggi" replace />} />
    </Routes>
  );
}

function ToasterResponsive() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const on = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  const [coda, setCoda] = useState(false);
  useEffect(() => ascoltaCoda((v) => setCoda(v.length > 0)), []);
  return <Toaster position={mobile ? "bottom-center" : "bottom-right"} duration={6000} closeButton offset={{ bottom: coda ? 340 : 88, right: 16 }} mobileOffset={{ bottom: coda ? 440 : 96 }} theme="dark" />;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <ToasterResponsive />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
